import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import logger from '@/lib/logger';
import { ArrowLeft, ChatCircle, Check } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null); setSuccess(null);
    try {
      const { data } = await axios.post(`${API}/support/contact`, form);
      setSuccess(data.ticket_id);
      setForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      logger.error('Contact submit:', err);
      setError(err?.response?.data?.detail || 'Could not send. Please try again.');
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white px-4 sm:px-6 py-10" data-testid="contact-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors mb-8 text-sm" data-testid="contact-back-link">
          <ArrowLeft weight="bold" className="w-4 h-4" /> Back to GRIT.FIT
        </Link>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-12 h-12 bg-[#FF3B30]/10 border border-[#FF3B30]/30 flex items-center justify-center">
            <ChatCircle weight="fill" className="w-6 h-6 text-[#FF3B30]" />
          </div>
          <div>
            <div className="label-tactical text-[#FF3B30]">Support</div>
            <h1 className="text-4xl sm:text-5xl font-black tracking-tighter">CONTACT</h1>
          </div>
        </div>
        <p className="text-[#A1A1AA] text-sm sm:text-base mb-8 leading-relaxed">
          Bug reports, feature requests, billing questions, account deletion — we read every message. We'll get back to you within 48 hours.
        </p>

        {success && (
          <div className="bg-[#34C759]/10 border border-[#34C759]/30 text-[#34C759] px-4 py-3 mb-6 text-sm flex items-center gap-2" data-testid="contact-success">
            <Check weight="bold" className="w-4 h-4" />
            Message received. Ticket <span className="font-black font-mono">#{success}</span> — check your email within 48 hours.
          </div>
        )}
        {error && (
          <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] px-4 py-3 mb-6 text-sm" data-testid="contact-error">
            {error}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label htmlFor="contact-name" className="label-tactical text-[#A1A1AA] mb-1 block">Your name</label>
            <input
              id="contact-name"
              data-testid="contact-name"
              type="text"
              required
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full bg-[#1C1C1E] border border-white/10 text-white px-4 py-3 outline-none focus:border-[#FF3B30] transition-colors"
            />
          </div>
          <div>
            <label htmlFor="contact-email" className="label-tactical text-[#A1A1AA] mb-1 block">Email</label>
            <input
              id="contact-email"
              data-testid="contact-email"
              type="email"
              required
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="w-full bg-[#1C1C1E] border border-white/10 text-white px-4 py-3 outline-none focus:border-[#FF3B30] transition-colors"
            />
          </div>
          <div>
            <label htmlFor="contact-subject" className="label-tactical text-[#A1A1AA] mb-1 block">Subject</label>
            <input
              id="contact-subject"
              data-testid="contact-subject"
              type="text"
              required
              value={form.subject}
              onChange={e => setForm(f => ({ ...f, subject: e.target.value }))}
              className="w-full bg-[#1C1C1E] border border-white/10 text-white px-4 py-3 outline-none focus:border-[#FF3B30] transition-colors"
            />
          </div>
          <div>
            <label htmlFor="contact-message" className="label-tactical text-[#A1A1AA] mb-1 block">Message</label>
            <textarea
              id="contact-message"
              data-testid="contact-message"
              required
              rows={6}
              value={form.message}
              onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
              className="w-full bg-[#1C1C1E] border border-white/10 text-white px-4 py-3 outline-none focus:border-[#FF3B30] transition-colors resize-vertical"
              placeholder="Tell us what's going on. Include as much detail as possible."
            />
          </div>
          <button
            type="submit"
            disabled={busy}
            className="btn-primary px-6 py-3 text-sm disabled:opacity-50"
            data-testid="contact-submit"
          >
            {busy ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>
    </div>
  );
}
